import { useClientPortalSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  Container,
  Divider,
  ProgressLoader,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { WorkdayIntegrationContext } from "../../WorkdayIntegrationContext";
import { WorkdayIntegrationStepLayout } from "../WorkdayIntegrationStepLayout";
import { CopiableReadonlyValue } from "./CopiableReadonlyValue";

export function EndpointsAndSecretsStep() {
  const { t } = useTranslation();
  const { goToNextStep, workdayConfig } = useContext(WorkdayIntegrationContext);

  const { submit, isSubmitting } = useClientPortalSubmit(
    "/api/internal/client-portal/ats-integrations/workday/validate-incoming-auth/",
    {
      onSuccess: () => {
        showToast(
          t("Certn has successfully connected with your Workday account"),
          {
            type: "success",
          }
        );
        goToNextStep();
      },
      showToastOnError: true,
    }
  );

  if (!workdayConfig) return;

  const {
    token_endpoint,
    incoming_webhook_endpoint,
    client_id,
    client_secret,
    refresh_token,
    access_token,
  } = workdayConfig;

  return (
    <WorkdayIntegrationStepLayout
      onComplete={() => submit(workdayConfig)}
      submitting={isSubmitting}
    >
      <Container
        layout="narrow"
        testID="lever-details-page-outermost-container"
        className={styles.container}
      >
        <Stack gap="space-2">
          <div>
            <Text style="input/section-title">
              {t("step {{stepNumber}} of {{stepCount}}", {
                stepNumber: 2,
                stepCount: 3,
              })}
            </Text>
          </div>
          <Text
            as="h1"
            className={styles.titleSpacing}
            style="global/title-small"
          >
            {t("{{integration}} integration", { integration: "Workday" })}
          </Text>
          <ProgressLoader floating value={67} />
        </Stack>
        <Stack gap="space-4">
          <Text style="global/heading-medium">
            {t("Details to add to your {{integration}} app", {
              integration: "Workday",
            })}
          </Text>
          <CopiableReadonlyValue
            value={token_endpoint}
            label={t("Token endpoint")}
          />
          <CopiableReadonlyValue
            value={incoming_webhook_endpoint}
            label={t("REST API endpoint")}
          />
          <Divider marginTop="space-6" />
          <CopiableReadonlyValue value={client_id} label={t("Client ID")} />
          <CopiableReadonlyValue value={client_secret} label={t("Secret")} />
          <CopiableReadonlyValue
            value={access_token}
            label={t("Access token")}
          />
          <CopiableReadonlyValue
            value={refresh_token}
            label={t("Refresh token")}
          />
        </Stack>
        <div className={styles.mainContentContainer}></div>
      </Container>
    </WorkdayIntegrationStepLayout>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
};
